import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UserPlus, Upload } from 'lucide-react';
import { PilgrimGuideList } from '../components/pilgrimGuide/PilgrimGuideList';
import { AddPilgrimModal } from '../components/pilgrimGuide/AddPilgrimModal';
import { BulkLinkModal } from '../components/pilgrimGuide/BulkLinkModal';
import { BulkTransferModal } from '../components/pilgrimGuide/modal/BulkTransferModal';
import { usePilgrimGuideStore } from '../stores/pilgrimGuideStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useLanguage } from '../contexts/LanguageContext';
import { canBulkLink, canBulkTransfer, canCreateLink, isGuide } from '../utils/roleChecks';

export const PilgrimGuidePage = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isBulkTransferOpen, setIsBulkTransferOpen] = useState(false);
  const { translations } = useLanguage();
  const { loading, error, fetchLinks } = usePilgrimGuideStore();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    if (userRole && userId) {
      const guideId = userRole === 'guide' ? userId : undefined;
      fetchLinks(guideId);
    }
  }, [userRole, userId, fetchLinks]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <UserPlus className="text-primary-500" size={24} />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              {translations?.pilgrimGuide?.header || 'Link Pilgrims to Guides'}
            </h1>
            <p className="text-sm md:text-base text-gray-500 mt-1">
              {userRole === 'guide'
                ? translations?.pilgrimGuide?.guideDescription ||
                  'Add pilgrims to your supervision list.'
                : translations?.pilgrimGuide?.adminDescription ||
                  'Link pilgrims to suitable guides and manage their relationship.'}
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          {/* Bulk features - Hidden on mobile */}
          <div className="hidden md:flex gap-3">
            {canBulkLink() && (
              <button
                onClick={() => setIsBulkModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <Upload size={20} />
                <span>{translations?.pilgrimGuide?.bulkLink || 'Bulk Link'}</span>
              </button>
            )}

            {canBulkTransfer() && (
              <button
                onClick={() => setIsBulkTransferOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <Upload size={20} />
                {translations?.pilgrimGuide?.bulktransfer || 'Bulk transfer'}
              </button>
            )}
          </div>

          {(canCreateLink() || isGuide()) && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="w-full md:w-auto bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600
                       transition-colors flex items-center justify-center gap-2"
            >
              <UserPlus size={20} />
              <span>
                {isGuide()
                  ? translations?.pilgrimGuide?.addButton || 'Add Pilgrim'
                  : translations?.pilgrimGuide?.linkButton ||
                    'Link Pilgrim to Guide'}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <PilgrimGuideList />
      </div>

      {/* Modals */}
      <AddPilgrimModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />

      <BulkLinkModal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
      />

      <BulkTransferModal
        isOpen={isBulkTransferOpen}
        onClose={() => setIsBulkTransferOpen(false)}
      />
    </div>
  );
};
